import { NotificationType } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Delivery from '~/client/src/shared/models/Delivery'
import BaseNotification from '~/client/src/shared/models/Notification'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import {
  isDeliveryType,
  isPermitType,
  isScannerDeactivatedType,
  isWorkflowMessageType,
} from '~/client/src/shared/types/NotificationTypes'
import isUserBicForCurrentStep from '~/client/src/shared/utils/isUserBicForCurrentStep'

// --------------- EntityData discriminated union ---------------

export type EntityData =
  | { kind: 'permit'; permit: Workflow | null }
  | { kind: 'delivery'; delivery: Delivery | null }
  | { kind: 'unknown' }

// --------------- Store interfaces for helper params ---------------

export interface IEntityStores {
  deliveriesStore: DeliveriesStore
}

export interface IDisplayStores {
  companiesStore: CompaniesStore
  permitTypesStore: WorkflowTypesStore
  projectMembersStore: ProjectMembersStore
  projectDateStore: ProjectDateStore
  zonesStore: ZonesStore
  offloadingEquipmentsStore: OffloadingEquipmentsStore
  locationAttributesStore: LocationAttributesStore
  messagesStore: MessagesStore
}

// --------------- isNotificationImportant ---------------

const IMPORTANT_MENTION_TYPES: NotificationType[] = [
  NotificationType.MessageReceived,
  NotificationType.PermitMessageReceived,
]

export const SITE_COMM_TYPES: NotificationType[] = [
  NotificationType.ClosureStarted,
  NotificationType.ClosureEnded,
  NotificationType.ClosureFollowed,
]

export type ImportantKind = 'bic' | 'announcement' | 'mention' | null

export function isNotificationImportant(
  n: BaseNotification,
  userId: string | undefined,
  workflowTypesStore: WorkflowTypesStore | undefined,
  permitBicStore: PermitBallInCourtStore | undefined,
): boolean {
  return (
    getImportantKind(n, userId, workflowTypesStore, permitBicStore) !== null
  )
}

export function getImportantKind(
  n: BaseNotification,
  userId: string | undefined,
  workflowTypesStore: WorkflowTypesStore | undefined,
  permitBicStore: PermitBallInCourtStore | undefined,
): ImportantKind {
  // BIC: user is an assignee on the current workflow step
  if (
    userId &&
    isPermitType(n.type) &&
    n.associatedPermit &&
    isUserBicForCurrentStep(
      userId,
      n.associatedPermit,
      workflowTypesStore,
      permitBicStore,
    )
  ) {
    return 'bic'
  }

  // Announcement/closure workflow type
  if (isPermitType(n.type) && n.associatedPermit) {
    const permitType = workflowTypesStore?.getPermitTypeById(
      n.associatedPermit.typeId,
    )
    if (permitType?.type === 'announcement' || permitType?.type === 'closure') {
      return 'announcement'
    }
  }

  // @ mentioned
  if (IMPORTANT_MENTION_TYPES.includes(n.type)) {
    return 'mention'
  }

  if (n.type === NotificationType.NewUserCreatedViaSignUp) {
    return 'bic'
  }

  return null
}

// --------------- Entity resolution ---------------

export function resolveEntityData(
  notification: BaseNotification,
  stores: IEntityStores,
): EntityData {
  const { type, entityId, associatedPermit } = notification

  if (isPermitType(type) || isWorkflowMessageType(type)) {
    return { kind: 'permit', permit: associatedPermit }
  }
  if (isDeliveryType(type)) {
    return {
      kind: 'delivery',
      delivery: stores.deliveriesStore.byId.get(entityId) ?? null,
    }
  }
  return { kind: 'unknown' }
}

// --------------- Data helpers ---------------

export function getCodeAndTitle(
  data: EntityData,
  notification: BaseNotification,
  companiesStore: CompaniesStore,
): string {
  switch (data.kind) {
    case 'permit': {
      if (!data.permit) return ''
      return `${data.permit.codeToDisplay()}${
        data.permit.title ? `: ${data.permit.title}` : ''
      }`
    }
    case 'delivery':
      if (!data.delivery) return ''
      return data.delivery.codeToDisplay(companiesStore)
    default:
      if (notification.type === NotificationType.NewUserCreatedViaSignUp) {
        return 'User Access Request'
      }
      return notification.type?.replace(/([A-Z])/g, ' $1').trim() || ''
  }
}

export function getEntityTypeName(
  data: EntityData,
  workflowTypesStore: WorkflowTypesStore,
): string {
  switch (data.kind) {
    case 'permit': {
      const permitType = workflowTypesStore?.getPermitTypeById(
        data.permit?.typeId,
      )
      return permitType?.name || ''
    }
    case 'delivery':
      return Localization.translator.delivery || 'Delivery'
    default:
      return ''
  }
}

export function getCompanyName(
  data: EntityData,
  companiesStore: CompaniesStore,
): string {
  switch (data.kind) {
    case 'delivery':
      return data.delivery
        ? companiesStore.getCompanyNameById(data.delivery.company)
        : ''
    case 'permit':
      return (
        data.permit?.companyIds
          ?.map(id => companiesStore.getCompanyNameById(id))
          .filter(Boolean)
          .join(', ') || ''
      )
    default:
      return ''
  }
}

export function getDateDisplay(
  data: EntityData,
  projectDateStore: ProjectDateStore,
): string {
  switch (data.kind) {
    case 'delivery':
      if (!data.delivery) return ''
      return projectDateStore.getCompactDateTimeInterval(
        data.delivery.startDate,
        data.delivery.endDate,
      )
    case 'permit':
      if (!data.permit) return ''
      return projectDateStore.getCompactDateTimeInterval(
        data.permit.startDate,
        data.permit.endDate,
      )
    default:
      return ''
  }
}

export function getEquipmentText(
  data: EntityData,
  offloadingEquipmentsStore: OffloadingEquipmentsStore,
): string {
  if (data.kind === 'delivery' && data.delivery) {
    const text = offloadingEquipmentsStore.getString(
      data.delivery.offloadingEquipments,
      data.delivery.isDone,
    )
    return text && text !== '—' ? text : ''
  }

  return ''
}

export function getLocationTagData(
  data: EntityData,
  locationAttributesStore: LocationAttributesStore,
  zonesStore: ZonesStore,
):
  | {
      kind: 'permit'
      locations: ReturnType<LocationAttributesStore['getById']>[]
    }
  | { kind: 'delivery'; zoneName: string }
  | null {
  if (data.kind === 'permit') {
    const rawLocations = data.permit?.locationsWithEquipment
    if (!rawLocations?.length) return null
    const locations = rawLocations
      .map(loc => locationAttributesStore?.getById(loc.id))
      .filter(Boolean)
    if (!locations.length) return null
    return { kind: 'permit', locations }
  }

  if (data.kind === 'delivery' && data.delivery) {
    const zoneName = zonesStore.byId.get(data.delivery.zone)?.name
    if (!zoneName) return null
    return { kind: 'delivery', zoneName }
  }

  return null
}

// --------------- Author & Action text ---------------

export function getAuthorAndAction(
  notification: BaseNotification,
  data: EntityData,
  projectMembersStore: ProjectMembersStore,
  messagesStore: MessagesStore,
): string {
  const { type, authorId } = notification

  if (type === NotificationType.NewUserCreatedViaSignUp) {
    const entityUser =
      projectMembersStore.getById(notification.entityId) ||
      projectMembersStore.getById(authorId)
    if (entityUser) {
      const name = User.getFullName(entityUser)
      const email = entityUser.email || ''
      return email
        ? `${name} (${email}) requested access`
        : `${name} requested access`
    }
    return 'New user requested access'
  }

  const author = projectMembersStore.getById(authorId)
  const authorName = author ? User.getFullName(author) : ''

  let actionText = ''

  if (isPermitType(type) || isWorkflowMessageType(type)) {
    actionText = getWorkflowActionText(type)
  } else if (isDeliveryType(type)) {
    actionText = getDeliveryActionText(type)
  } else if (isScannerDeactivatedType(type)) {
    actionText = Localization.translator.scannerDeactivated
  }

  const base = authorName ? `${authorName} ${actionText}`.trim() : actionText

  if (isWorkflowMessageType(type)) {
    const message = messagesStore?.byId.get(notification.messageId)
    if (message?.notificationText) {
      return `${base}: ${message.notificationText}`
    }
  }

  return base
}

export function getWorkflowActionText(type: NotificationType): string {
  switch (type) {
    case NotificationType.PermitAccepted:
      return Localization.translator.workflowApproved
    case NotificationType.PermitClosed:
      return Localization.translator.workflowDone
    case NotificationType.PermitSubmitted:
      return Localization.translator.workflowSubmitted
    case NotificationType.PermitChanged:
      return Localization.translator.workflowChanged
    case NotificationType.PermitDenied:
      return Localization.translator.workflowDenied
    case NotificationType.PermitToInspect:
      return Localization.translator.workflowReadyToInspect
    case NotificationType.PermitActivated:
      return Localization.translator.workflowActivated
    case NotificationType.PermitReviewed:
      return Localization.translator.workflowReviewed
    case NotificationType.PermitOnsite:
      return Localization.translator.workflowOnSite
    case NotificationType.PermitFailed:
      return Localization.translator.workflowFailed
    case NotificationType.PermitPassed:
      return Localization.translator.workflowPassed
    case NotificationType.PermitDeleted:
      return Localization.translator.workflowDeleted
    case NotificationType.PermitEnded:
      return Localization.translator.workflowEnded
    case NotificationType.PermitFollowed:
      return Localization.translator.workflowFollowed
    case NotificationType.PermitMessageReceived:
      return Localization.translator.workflowMessageAdded
    default:
      return ''
  }
}

export function getDeliveryActionText(type: NotificationType): string {
  switch (type) {
    case NotificationType.DeliveryRequested:
      return Localization.translator.deliveryRequested
    case NotificationType.DeliveryScheduled:
      return Localization.translator.deliveryScheduled
    case NotificationType.DeliveryOnsite:
      return Localization.translator.deliveryOnSite
    case NotificationType.DeliveryOnhold:
      return Localization.translator.deliveryOnHold
    case NotificationType.DeliveryPaused:
      return Localization.translator.deliveryPaused
    case NotificationType.DeliveryDelivering:
      return Localization.translator.delivering
    case NotificationType.DeliveryPassedInspection:
      return Localization.translator.deliveryPassedInspection
    case NotificationType.DeliveryFailedInspection:
      return Localization.translator.deliveryFailedInspection
    case NotificationType.DeliveryDone:
      return Localization.translator.deliveryDone
    case NotificationType.DeliveryIncompleteDone:
      return Localization.translator.deliveryIncompletelyDone
    case NotificationType.DeliveryUpdated:
      return Localization.translator.deliveryUpdated
    case NotificationType.DeliveryDeleted:
      return Localization.translator.workflowDeleted
    case NotificationType.DeliveryDenied:
      return Localization.translator.deliveryDenied
    case NotificationType.DeliveryCanceled:
      return Localization.translator.deliveryCanceled
    case NotificationType.DeliveryFollowed:
      return Localization.translator.deliveryFollowed
    case NotificationType.DeliveryRequestedByLocationClosure:
      return Localization.translator.deliveryRequested
    default:
      return ''
  }
}

export function getUserSignupMeta(
  notification: BaseNotification,
  projectMembersStore: ProjectMembersStore,
  companiesStore?: CompaniesStore,
  userProjectsStore?: UserProjectsStore,
): {
  companyId: string | null
  companyName: string
  userName: string
} {
  if (notification.type !== NotificationType.NewUserCreatedViaSignUp) {
    return { companyId: null, companyName: '', userName: '' }
  }

  const user =
    projectMembersStore.getById(notification.entityId) ||
    projectMembersStore.getById(notification.authorId)
  if (!user) {
    return { companyId: null, companyName: '', userName: '' }
  }

  const userProject = userProjectsStore?.getByUser(user)
  const assignedCompanyId = userProject?.companyId || null
  const assignedCompany = assignedCompanyId
    ? companiesStore?.getCompanyById(assignedCompanyId)
    : null

  return {
    companyId: assignedCompany ? assignedCompanyId : null,
    companyName: assignedCompany?.name || user.companyAlias || '',
    userName: User.getFullName(user),
  }
}

export function matchesImportantTabFilter(
  n: BaseNotification,
  userId: string | undefined,
  workflowTypesStore: WorkflowTypesStore | undefined,
  permitBicStore: PermitBallInCourtStore | undefined,
): boolean {
  if (isNotificationImportant(n, userId, workflowTypesStore, permitBicStore)) {
    return true
  }

  return SITE_COMM_TYPES.includes(n.type)
}

// --------------- Meta text helpers ---------------

export function getInterpunctMeta(
  data: EntityData,
  stores: IDisplayStores,
): { darkParts: string[]; lightParts: string[] } {
  const typeName = getEntityTypeName(data, stores.permitTypesStore)
  const companyName = getCompanyName(data, stores.companiesStore)
  const dateDisplay = getDateDisplay(data, stores.projectDateStore)
  const equipmentText = getEquipmentText(data, stores.offloadingEquipmentsStore)

  return {
    darkParts: [typeName, companyName].filter(Boolean),
    lightParts: [dateDisplay, equipmentText].filter(Boolean),
  }
}
